import axios from 'axios'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { Field, FieldDescription, FieldError, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { AddressFields } from '../../../../components/AddressFields'
import {
  certifiedPolicyIds,
  type COICertificateHolder,
  createCertificateHolder,
  type CreateCertificateHolderData,
  documentsForSelection,
  remarksEnabled,
  sendCOI,
  updateCertificateHolder,
  uploadCertificateHolderDocument,
  useCurrentCOIDocuments,
} from '../../../../models/coi'
import { CoiMode } from '../../../../models/insurancePolicy'
import { useCurrentUser, useOrganisation } from '../../../../models/user'
import { DescriptionOfOperationsNotice } from './DescriptionOfOperationsNotice'
import { EndorsementWarning } from './EndorsementWarning'
import { PolicySelectionPicker } from './PolicySelectionPicker'

interface AddCertificateHolderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  holder?: COICertificateHolder
  orgId?: string
  manualMode?: boolean
}

interface FormData {
  name: string
  email: string
  line1: string
  line2: string
  city: string
  state: string
  zip: string
  descriptionOfOperations: string
  autoRenew: boolean
  includeTrucks: boolean
  includeTrailers: boolean
}

export function AddCertificateHolderDialog({
  open,
  onOpenChange,
  onSuccess,
  holder,
  orgId,
  manualMode,
}: AddCertificateHolderDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  const isEditing = !!holder
  const { organisation } = useOrganisation(orgId)
  const { user: currentUser } = useCurrentUser({ enabled: !orgId })
  const policies = (orgId ? organisation?.insurancePolicies : currentUser?.currentOrg.insurancePolicies) ?? []
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([])
  // With one policy there is nothing to choose, so the step is skipped and the
  // backend's default selection applies.
  const needsPolicyChoice = !isEditing && policies.length > 1
  // An automatic policy has nothing to issue until its certificate exists, so the
  // picker offers only the policies that have one. Fetched whenever the dialog is
  // open, not just when there is a choice to make, because the certificate also
  // decides whether a vehicle schedule may be offered at all.
  const { documents } = useCurrentCOIDocuments(orgId, { enabled: open })
  const certifiedIds = certifiedPolicyIds(documents)
  const selectedPolicies = policies.filter((policy) => selectedPolicyIds.includes(policy.id))
  const selectionIsManual = selectedPolicies.some((policy) => policy.coiMode === CoiMode.MANUAL)
  // A manual selection means the document is requested from a Cai admin, which
  // is the same flow the org-wide manual mode used.
  const manualEntry = (!!manualMode || selectionIsManual) && !isEditing
  // Uploading a COI by hand stops it being regenerated, so its schedule switches would be decoration.
  const documentIsManual = !!holder && !holder.isAutomatic
  // Which certificate this holder lands on decides whether a vehicle schedule
  // may be offered at all. Before a selection is picked that is every
  // certificate the org holds, so the choice is hidden only when none allows
  // one — a certificate we cannot identify must not silently drop the switches.
  const candidateDocuments = documentsForSelection(documents, {
    selectionKey: holder?.selectionKey,
    policyIds: selectedPolicyIds,
  })
  const canScheduleVehicles = !candidateDocuments.length || candidateDocuments.some(remarksEnabled)

  const { control, handleSubmit, reset, setError, setValue } = useForm<FormData>({
    defaultValues: {
      name: '',
      email: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      zip: '',
      descriptionOfOperations: '',
      autoRenew: false,
      includeTrucks: false,
      includeTrailers: false,
    },
  })

  useEffect(() => {
    if (open && holder) {
      reset({
        name: holder.name,
        email: holder.email,
        line1: holder.address?.line1 ?? '',
        line2: holder.address?.line2 ?? '',
        city: holder.address?.city ?? '',
        state: holder.address?.state ?? '',
        zip: holder.address?.zip ?? '',
        descriptionOfOperations: holder.descriptionOfOperations ?? '',
        autoRenew: holder.autoRenew,
        includeTrucks: holder.includeTrucks,
        includeTrailers: holder.includeTrailers,
      })
    }
  }, [open, holder, reset])

  function handleClose(isOpen: boolean) {
    if (!isOpen) {
      setSelectedPolicyIds([])
      reset()
      setFile(null)
    }
    onOpenChange(isOpen)
  }

  async function onSubmit(data: FormData, sent: boolean) {
    setIsSubmitting(true)
    try {
      // The address is optional, so send it only when something was entered —
      // otherwise every holder without one would get an empty Address record.
      const hasAddress = !!(data.line1 || data.line2 || data.city || data.state || data.zip)
      const payload: CreateCertificateHolderData = {
        policyIds: selectedPolicyIds.length ? selectedPolicyIds : undefined,
        name: data.name,
        email: data.email,
        autoRenew: data.autoRenew,
        // Forced off, not just hidden: editing a holder that had a schedule
        // before the certificate forbade one would otherwise post the old value
        // straight back. The API applies the same rule.
        includeTrucks: documentIsManual ? undefined : canScheduleVehicles && data.includeTrucks,
        includeTrailers: documentIsManual ? undefined : canScheduleVehicles && data.includeTrailers,
        // Omitted entirely for a carrier: the field is hidden from them, but an
        // edit still holds the description we wrote, and sending it back would
        // ask the API to rewrite it with our own text.
        //
        // Sent as-is for an admin, empty string included. Collapsing "" to
        // undefined drops the key from the PATCH body, and the API treats an
        // absent key as "leave it alone" — so clearing the box saved the old
        // wording straight back and looked like nothing happened.
        descriptionOfOperations: isCaiAdmin ? data.descriptionOfOperations : undefined,
        ...(hasAddress
          ? {
              address: {
                line1: data.line1,
                // Also sent as-is, for the same reason: undefined would drop the
                // key and leave a cleared second line on the address.
                line2: data.line2,
                city: data.city,
                state: data.state,
                zip: data.zip,
              },
            }
          : {}),
      }

      if (manualEntry) {
        const created = await createCertificateHolder({ ...payload, autoRenew: false }, orgId)
        if (isCaiAdmin && file) {
          await uploadCertificateHolderDocument(created.id, file, orgId)
        }
        toast.success(isCaiAdmin ? 'Certificate holder added' : 'Certificate holder requested')
        onSuccess()
        handleClose(false)
        return
      }

      if (isEditing) {
        await updateCertificateHolder(holder.id, payload, orgId)
        toast.success('Certificate holder updated')
      } else {
        const created = await createCertificateHolder(
          { ...payload, send: file ? false : sent, skipGeneration: !!file },
          orgId
        )
        if (file) {
          try {
            await uploadCertificateHolderDocument(created.id, file, orgId)
            if (sent) await sendCOI(created.id, orgId)
          } catch {
            toast.error(
              'Certificate holder was added, but the COI upload failed. Open the holder to upload it again.'
            )
            onSuccess()
            handleClose(false)
            return
          }
        }
        toast.success(sent ? 'Certificate holder added and COI sent' : 'Certificate holder added')
      }
      onSuccess()
      handleClose(false)
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.email) {
        setError('email', { message: error.response.data.email[0] })
      } else {
        toast.error(isEditing ? 'Failed to update certificate holder' : 'Failed to add certificate holder')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent size="lg" className="overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? 'Edit certificate holder'
              : manualEntry && !isCaiAdmin
                ? 'Request certificate holder'
                : 'Add certificate holder'}
          </DialogTitle>
          {!isEditing && (
            <DialogDescription>
              {manualEntry
                ? isCaiAdmin
                  ? 'Upload the COI document for this certificate holder.'
                  : "We'll prepare the COI document for this certificate holder and let you know once it's ready to send."
                : "We'll generate and send an official COI once the details of certificate holder are confirmed."}
            </DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit((data) => onSubmit(data, false))} className="flex flex-col gap-6">
          <FieldGroup>
            {needsPolicyChoice && (
              <Field>
                <Label>Policies</Label>
                <FieldDescription>
                  Which certificate this holder receives. A manually issued policy is always on its own, and a
                  policy is unavailable until its certificate has been generated.
                </FieldDescription>
                <PolicySelectionPicker
                  policies={policies}
                  selectedIds={selectedPolicyIds}
                  certifiedIds={certifiedIds}
                  onChange={setSelectedPolicyIds}
                />
              </Field>
            )}

            {needsPolicyChoice && !selectedPolicyIds.length ? (
              <FieldDescription>Pick the policies above to continue.</FieldDescription>
            ) : (
              <>
                <Field>
                  <Label htmlFor="holder-name">Certificate holder name</Label>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        id="holder-name"
                        placeholder="Certificate holder name"
                        aria-invalid={fieldState.error ? true : undefined}
                      />
                    )}
                  />
                </Field>

                <Field>
                  <Label htmlFor="holder-email">Email</Label>
                  <FieldDescription>
                    Add several addresses separated by commas — the COI is sent to all of them.
                  </FieldDescription>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState }) => (
                      <>
                        <Input
                          {...field}
                          id="holder-email"
                          type="text"
                          placeholder="ap@broker.com, dispatch@broker.com"
                          aria-invalid={fieldState.error ? true : undefined}
                        />
                        {fieldState.error?.message && <FieldError>{fieldState.error.message}</FieldError>}
                      </>
                    )}
                  />
                </Field>

                {!isEditing && isCaiAdmin && (
                  <Field>
                    <Label>{manualEntry ? 'COI document' : 'COI document (optional)'}</Label>
                    {!manualEntry && (
                      <FieldDescription>
                        When attached, this document is used as-is instead of generating the COI
                        automatically.
                      </FieldDescription>
                    )}
                    <Dropzone onChange={setFile} accept=".pdf" maxSizeMB={10} />
                  </Field>
                )}

                {/* Optional: an imported holder list often carries no address at all
                (the broker export this was built for has no address column), and
                requiring one would block editing every holder that came from a
                file. The address is only used to fill the certificate holder box
                on the PDF, which tolerates being blank. */}
                <AddressFields
                  control={control}
                  setValue={setValue}
                  names={{ line1: 'line1', line2: 'line2', city: 'city', state: 'state', zip: 'zip' }}
                  label="Address (optional)"
                />

                {/* Cai staff only — the wording on an issued certificate is
                    underwriting language we answer for, so a carrier asks us for
                    it instead (DescriptionOfOperationsNotice, below). */}
                {isCaiAdmin && (
                  <Field>
                    <Label htmlFor="holder-description">Description of operations (optional)</Label>
                    <FieldDescription>
                      Leave blank to use the certificate&apos;s own description of operations.
                    </FieldDescription>
                    <Controller
                      name="descriptionOfOperations"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          id="holder-description"
                          placeholder="Description of operations"
                          className="h-24"
                        />
                      )}
                    />
                  </Field>
                )}

                {!manualEntry && (
                  <>
                    {canScheduleVehicles && documentIsManual && (
                      <FieldDescription>
                        {holder.hasDocument
                          ? "This holder's COI is uploaded by hand, so its vehicle schedule comes from the uploaded document."
                          : "This holder's COI is issued by hand, so its vehicle schedule is set on the document Cai uploads."}
                      </FieldDescription>
                    )}

                    {canScheduleVehicles && !documentIsManual && (
                      <>
                        <Field orientation="horizontal">
                          <div className="flex-1">
                            <Label className="text-md mb-1">Include trucks in vehicle schedule</Label>
                            <FieldDescription className="text-sm">
                              Show the org's trucks on the ACORD 101 vehicle schedule page of this holder's
                              COI.
                            </FieldDescription>
                          </div>
                          <Controller
                            name="includeTrucks"
                            control={control}
                            render={({ field }) => (
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            )}
                          />
                        </Field>

                        <Field orientation="horizontal">
                          <div className="flex-1">
                            <Label className="text-md mb-1">Include trailers in vehicle schedule</Label>
                            <FieldDescription className="text-sm">
                              Show the org's trailers on the ACORD 101 vehicle schedule page of this holder's
                              COI.
                            </FieldDescription>
                          </div>
                          <Controller
                            name="includeTrailers"
                            control={control}
                            render={({ field }) => (
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            )}
                          />
                        </Field>
                      </>
                    )}

                    <Field orientation="horizontal" className="hidden">
                      <div className="flex-1">
                        <Label className="text-md mb-1">Automatic COI renewal</Label>
                        <FieldDescription className="text-md">
                          The new COI will be automatically sent to this certificate holder, when your policy
                          is updated or renews next year.
                        </FieldDescription>
                      </div>
                      <Controller
                        name="autoRenew"
                        control={control}
                        render={({ field }) => (
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        )}
                      />
                    </Field>
                  </>
                )}
              </>
            )}
          </FieldGroup>

          {/* Both stated together at the foot of the form: they are the two
              things on a certificate a carrier cannot word themselves. */}
          {!isEditing && !manualMode && !selectionIsManual && <EndorsementWarning />}
          {!isCaiAdmin && (!needsPolicyChoice || !!selectedPolicyIds.length) && (
            <DescriptionOfOperationsNotice />
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="link" type="button">
                Cancel
              </Button>
            </DialogClose>
            {needsPolicyChoice && !selectedPolicyIds.length ? null : isEditing ? (
              <Button type="submit" disabled={isSubmitting}>
                Save changes
              </Button>
            ) : manualEntry ? (
              <Button
                type="submit"
                disabled={isCaiAdmin && !file}
                loading={
                  isSubmitting ? (isCaiAdmin ? 'Adding certificate holder…' : 'Requesting…') : undefined
                }
              >
                {isCaiAdmin ? 'Add certificate holder' : 'Request certificate holder'}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleSubmit((data) => onSubmit(data, true))}
                >
                  Add and send COI
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save info
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
